import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import LoadingIndicator from './components/LoadingIndicator';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import { scrollToHomeSectionWhenReady } from './utils/scrollToHomeSection';
import { onScroll } from './utils/onScroll';
import content from './content.json';
import './App.css';
import ScrollToTop from './components/ScrollToTop';

const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const AuthSuccessPage = lazy(() => import('./components/AuthSuccessPage'));
const PurchasePage = lazy(() => import('./pages/PurchasePage'));

const SECTIONS = ['welcome', 'about', 'watchmyedit', 'prepmymedia', 'viewmyattic', 'trackmypost', 'contact'];

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return;

    let cancelPendingScroll = () => {};

    const scrollToHash = (hash) => {
      cancelPendingScroll();
      if (hash) {
        cancelPendingScroll = scrollToHomeSectionWhenReady(hash);
      }
    };

    const handleHashChange = () => {
      scrollToHash(window.location.hash.substring(1));
    };

    scrollToHash(window.location.hash.substring(1));

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      cancelPendingScroll();
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of SECTIONS) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            const currentHash = window.location.hash.substring(1);
            if (currentHash !== sectionId) {
              window.history.replaceState(null, null, `#${sectionId}`);
            }
            break;
          }
        }
      }
    };

    return onScroll(handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();

        const currentSection = SECTIONS.find((section) => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });

        if (currentSection) {
          const currentIndex = SECTIONS.indexOf(currentSection);
          const nextIndex = event.key === 'ArrowDown'
            ? Math.min(currentIndex + 1, SECTIONS.length - 1)
            : Math.max(currentIndex - 1, 0);

          const nextSection = document.getElementById(SECTIONS[nextIndex]);
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<HomePage content={content} />} />
      <Route path="/apps/:productId" element={<ProductPage content={content} />} />
      <Route path="/apps/:productId/purchase/success" element={<PurchasePage content={content} />} />
      <Route path="/apps/:productId/purchase" element={<PurchasePage content={content} />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/auth-success" element={<AuthSuccessPage />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={<LoadingIndicator />}>
        <ErrorBoundary>
          <div className="App">
            <ScrollToTop />
            <AppContent />
          </div>
        </ErrorBoundary>
      </Suspense>
    </Router>
  );
}

export default App;
